import _cloneDeep from 'lodash/cloneDeep';
import {
  ARRAY_OPS,
  BOOLEAN_OPS,
  DATE_TYPES,
} from 'src/app/slicedForm/FilterForm/definitions/inputEnums';
import {
  getCurrentTradingDay,
  minutesOfDay,
  parseAssumeMarketTime
} from 'src/utils/datetime/date-fns.tz';
import { format } from 'date-fns';
import { STRUCTURAL_TYPES } from 'src/app/slicedForm/mapping/mappingDirections/index';
import { isRollingKey, getRangeDateStrings } from 'src/app/components/pickers/definitions/staticRangeDefinitions';


/**  @typedef {import('./mappingDirections/index.js').FormStruct} FormStruct */
/**  @typedef {import('./mappingDirections/index.js').ProfileNodeBinaryArg} ProfileNodeBinaryArg */
/**  @typedef {import('./mappingDirections/index.js').ProfileFilterNode} ProfileFilterNode */
/**  @typedef {import('./mappingDirections/index.js').ProfileGroupNode} ProfileGroupNode */
/**  @typedef {import('./mappingDirections/index.js').QueryNodeBinaryArg} QueryNodeBinaryArg */
/**  @typedef {import('./mappingDirections/index.js').QueryFilterNode} QueryFilterNode */

/**
 * TODO: 11/1/23
 *
 * Our mappingDirection classes assume the overall profiles collection as the base.
 * Because of that, profileToQuery doesn't fit. profileToQuery only applies to a single query.
 *
 * Rework the mappingDirections to use an individual profile as the base, and add the extra properties around it.
 */


/**
 * Individual queries must be mapped to the database format.
 * Not the entire profiles object, just a single one containing a query.
 */



/**
 * Map a query before sending it into the database.
 * Changes the following, in order. THE ORDER IS IMPORTANT.
 *  1) Resolves SLICE_GROUPS into a single filter (SLICE_GROUP[] -> FILTER_NODE{})
 *  2) Resolve node.left.allowNull into OR group, (column > ARG or col is NULL)
 *  3) Resolve 'range' rolling dates into real BETWEEN dates
 *  2) Replace BETWEEN with two ANDs (col > A AND col <= B)
 *  3) Remove extranious FilterNode keys (resolve)
 *  4) Remove array from right-hand arguments (flatten, node.right[] -> node.right{})
 *
 * @param {Object} filters - recursive tree structure
 * @param {string} day - yyyy-MM-dd
 * @param {string[]} excludedTickers - Component-Level filter to exclude tickers
 * @returns {Object} filters - Mapped to the database format
 */
export default function mapSingleProfileStructToQueryStruct(filters, day, excludedTickers = []) {
  if (!filters) {
    filters = { AND: [] };
  }

  filters = _cloneDeep(filters);

  if (day) {
    try {
      filters.AND.push({
        left: { column: 'day0_date', type: 'column' },
        operator: BOOLEAN_OPS.EQ,
        right: [{ value: day, type: 'value' }]
      });
    } catch (err) {
      console.warn('topListFormatQueriesV2() day: Filters.AND doesn\'t exist in top-level', filters);
    }
  }
  if (excludedTickers && excludedTickers.length) {
    try {
      filters.AND.push({
        left: { column: 'ticker', type: 'column' },
        operator: ARRAY_OPS.NIN,
        right: [{ value: excludedTickers, type: 'value' }]
      })
    } catch (err) {
      console.warn('topListFormatQueriesV2() excludedTickers: Filters.AND doesn\'t exist in top-level', filters);
    }
  }

  return modify(filters);
}


/**
 * @param {Object} node
 * @returns {keyof STRUCTURAL_TYPES}
 */
export const decideNodeType = (node) => {
  if (!node || !(typeof node === 'object')) return null;

  if ('operator' in node) return STRUCTURAL_TYPES.FILTER;
  if (STRUCTURAL_TYPES.AND in node) return STRUCTURAL_TYPES.AND;
  if (STRUCTURAL_TYPES.OR in node) return STRUCTURAL_TYPES.OR;
  if (STRUCTURAL_TYPES.SLICE_GROUP in node) return STRUCTURAL_TYPES.SLICE_GROUP;

  return null;
};


/**
 * SQL's BETWEEN isn't valuable for us. Its double-inclusive.
 * Replace a single BETWEEN clause with two OR clauses.
 *
 * The second clause will assume the first one's type
 *
 * We could do this serverside, but in the future we might want a real BETWEEN serverside.
 * @param {ProfileFilterNode} node
 * @param {Array<BOOLEAN_OPS>} operators - Which operators to replace with AND
 * @returns {ProfileGroupNode} - If transformable, return AND group
 * @throws Error - The node is not transformable
 */
export function replaceBetweenWithAnd(node, operators = [BOOLEAN_OPS.GE, BOOLEAN_OPS.LT]) {
  if (!Array.isArray(operators) || !operators.length === 2) {
    throw new Error('replaceBetweenWithAnd() requires an array of two BOOLEAN_OPS, one for each comparison');
  }
  if (!node.operator === BOOLEAN_OPS.BTW || !Array.isArray(node.right)) {
    throw new Error(`replaceBetweenWithAnd() called on non-BTW node, ${JSON.stringify(node)}`);
  }
  const argList = node.right.slice(0, 2);
  if (!argList[0] || !argList[1]) {
    throw new Error(`replaceBetweenWithAnd() called on BTW node with missing arguments. 0:${argList[0]} 1:${argList[1]}`);
  }
  return {
    AND: [
      {
        left: { ...node.left },
        operator: operators[0],
        right: [{ ...argList[0] }]
      },
      {
        left: { ...node.left },
        operator: operators[1],
        right: [{
          ...argList[1],
          // Assume the first one's type
          type: argList[0].type
        }]
      }
    ]
  }
}


export function resolveRollingDateRange(node) {
  if (!decideNodeType(node) === STRUCTURAL_TYPES.FILTER) {
    throw new Error('resolveRollingDateRange() called on non-filter node');
  }
  if (!node?.dateType === DATE_TYPES.ROLLING) {
    throw new Error('resolveRollingDateRange() called on non-rolling date');
  }
  let rollingKey = node.right[0]?.value;
  if (!isRollingKey(rollingKey)) {
    throw new Error(`resolveRollingDateRange() called on unknown rolling_key value. right[0].value: ${node?.right[0]?.value}, node: ${node}`);
  }
  // TODO: What about different formats? We'd have to pull in colDefs, but thats not a fixed value.
  // We could pass in colDefs on mapping functions? That probably makes the most sense. Ignore for now.

  // Rolling dates always reference TODAY as end date, for now. Ignore BTW, ignore endDate.
  const { startDate } = getRangeDateStrings(rollingKey, 'yyyy-MM-dd');
  const { dateType, ...leftRest } = node.left;
  return {
    ...node,
    left: {
      ...leftRest,
    },
    right: [{
      type: 'value',
      column: null,
      value: startDate,
    }]
  }
}


/**
 * FILTER entities right-hand arguments look like this client-side:
 *      { column: 'vol', value: 200, type: 'value' };
 *    Resolve them to a single property, based on 'type':
 *      { value: 200 }
 * @param {ProfileFilterNode} node
 * @returns {QueryFilterNode}
 */
export function resolveNodeBinaryArguments(node) {

  /**
   * Transform the full Arg to the shortened Query version
   * @param {ProfileNodeBinaryArg} side
   * @returns {QueryNodeBinaryArg}
   */
  const resolveArg = (side) => {
    if (!side) return {};

    if (side.type === 'column') {
      const { type, value, ...rest } = side;
      return rest;
    }
    if (side.type === 'value') {
      const { type, column, ...rest } = side;
      return rest;
    }
    console.warn('FilterNode Binary Arg type not recognized', JSON.stringify(node));
    return side;
  };

  let left = resolveArg(node.left);
  let right;
  if (Array.isArray(node.right)) {
    right = node.right.map(resolveArg);
  } else {
    right = resolveArg(node.right);
  }

  return { ...node, left, right };
}

/**
 * Right arguments are arrays. We need them to be flat. pick the first one.
 * Make sure you map BETWEEN to AND before this step, or you will lose data.
 *  node.right = [binaryArg] => node.right = binaryArg
 *  @param {QueryFilterNode} node
 *  @returns {QueryFilterNode}
 */
export function flattenRightArgumentArray(node) {
  if (!decideNodeType(node) === STRUCTURAL_TYPES.FILTER) {
    console.warn('flattenRightArgumentArray only accepts FILTER nodes, ignoring');
    return node;
  }
  if (node.operator === BOOLEAN_OPS.BTW) {
    console.warn('Found BTW query operator. This probably should have been replaced with AND. Ignoring.', node);
    return node;
  }
  if (!Array.isArray(node.right)) {
    return node;
  }
  return { ...node, right: node.right[0] }
}


/**
 * Take the current time, and pick the single SLICE that matches.
 * Remove all other slices. It becomes a single filter node.
 * @param {ProfileGroupNode} node - Slice Group node
 * @param {Date} now
 * @return {ProfileFilterNode} - Filer node that it contains, no slice group
 */
export function resolveSliceGroup(node, now) {
  let selectedNode = node.SLICE_GROUP[0];

  for (let i = 0; i < node.SLICE_GROUP.length; i++) {
    const sliceNode = node.SLICE_GROUP[i];
    const nextSliceNode = node.SLICE_GROUP[i + 1];

    if (nextSliceNode === undefined) {
      // last node, this has to be it
      // TODO: what about 00->04am?
      selectedNode = sliceNode;
      break;
    }

    const nowMinutes = minutesOfDay(now);
    const startTimeMinutes = minutesOfDay(parseAssumeMarketTime(sliceNode.startTime, 'HH:mm'));
    const nextStartTimeMinutes = minutesOfDay(parseAssumeMarketTime(nextSliceNode.startTime, 'HH:mm'));

    if (nowMinutes >= startTimeMinutes && nowMinutes < nextStartTimeMinutes) {
      selectedNode = sliceNode;
      break;
    }
  }

  /* eslint-disable-next-line no-unused-vars */
  const { startTime, ...newNode } = selectedNode;
  return newNode;
}


/**
 * On the node.left object, we may have an 'allowNull' prop.
 * This signifies the query should be (column > ARG or col is NULL).
 * As such, create the OR group structure.
 *
 * NOTE: This MUST happen before the BETWEEN logic is applied
 * @param {ProfileFilterNode} node
 * @return {ProfileGroupNode}
 * @throws Error - If the transformastion is not valid
 */
export function mapAllowNullToOrGroup(node) {
  if (!decideNodeType(node) === STRUCTURAL_TYPES.FILTER) {
    throw new Error('mapAllowNullToOrGroup() only accepts FILTER nodes, ignoring');
  }
  if (!node?.left?.allowNull) {
    throw new Error('mapAllowNullToOrGroup() requires node.left.allowNull to be true');
  }
  // remove allowNull flag
  const { allowNull, ...restLeft } = node.left;
  return {
    OR: [
      {
        ...node,
        left: { ...restLeft }
      },
      {
        left: { ...restLeft },
        operator: BOOLEAN_OPS.EQ,
        // instead of IS NULL, let ORM handle it with val == NULL
        right: [{ value: null, type: 'value', column: null }]
      }
    ]
  }
}


/**
 * Recurse the tree, and apply all modifications
 * @param {Object} filters - The root of the filters tree
 * @param {Date} now - localized
 */
function modify(
  filters,
  now = getCurrentTradingDay()
) {
  const recurse = (node) => {
    const type = decideNodeType(node);

    switch (type) {
      case STRUCTURAL_TYPES.AND:
      case STRUCTURAL_TYPES.OR: {
        return { [type]: node[type].map(recurse) };
      }
      case STRUCTURAL_TYPES.SLICE_GROUP: {
        const newNode = resolveSliceGroup(node, now);
        return recurse(newNode);
      }
      case STRUCTURAL_TYPES.FILTER: {
        let newNode = node;

        if (newNode?.left?.allowNull) {
          try {
            newNode = mapAllowNullToOrGroup(newNode);
            // the node is now an OR group. Keep recursing.
            return recurse(newNode)
          } catch (err) {
            console.warn(err)
          }
        }

        if (newNode?.dateType === DATE_TYPES.ROLLING) {
          try {
            newNode = resolveRollingDateRange(newNode);
            // No new group created, continue
          } catch (err) {
            console.warn(err)
          }
        }

        if (newNode.operator === BOOLEAN_OPS.BTW) {
          try {
            newNode = replaceBetweenWithAnd(newNode);
            // the node is now an AND group. Keep recursing.
            return recurse(newNode)
          } catch (err) {
            console.warn(err)
          }
        }

        newNode = resolveNodeBinaryArguments(newNode);

        return flattenRightArgumentArray(newNode);
      }
      default: {
        console.warn(JSON.stringify(filters));
        throw new Error(`Unknown node type "${type}", node ${JSON.stringify(node)}}. Check logs.`);
      }
    }
  };

  return recurse(filters);
}
